import {IWedEditorMetaBar, METAS} from "/@back@/edit/wed/features/metaBar.js";
import {InlLinkModel, ITxtElement, ITxtOnCreateHook, TxtColModel, TxtModel} from "/@back@/edit/wed/wedlets/txt/txt.js";
import {TxtCell, TxtCol, TxtElement} from "/@back@/edit/wed/wedlets/txt/txtTags.js";
import {ITxtCellSpanMgr, ITxtColWidthMgr} from "/@back@/edit/wed/wedlets/txt/txtUtils.js";
import {WEDLET} from "/@back@/edit/wed/wedlets/wedlet.js";
import {FastFindItem} from "/@back@/wsp/dialogs/fastFindItem.js";
import {IItemUiEnv} from "/@back@/wsp/views/itemMain.js";
import {IPtrItemRule} from "/@back@/wsp/widgets/wed/schemaMetaWsp.js";
import {DOM} from "/@lib@/commons/xml/dom.js";
import {IJmlNode, IJmlObj, IJmlSet, JML} from "/@lib@/commons/xml/jml.js";
import {XA} from "/@lib@/commons/xml/xAddr.js";
import {DocHolder, IDocBatch} from "/@lib@/edit/docHolder.js";
import {XmlStrMsg} from "/@lib@/edit/ot/xmlHouse.js";
import {SkRule, SkRuleDoc, SkRuleElt, SkRuleNode} from "/@lib@/edit/schema/schemaPatterns.js";
import {ITEM} from "/@lib@/wsp/item.js";
import {SRC} from "/@lib@/wsp/src.js";
import {IWspEnv} from "/@lib@/wsp/wsp.js";
import {ItemCreator} from "/@back@/wsp/dialogs/itemCreator.js";
import {SearchItemSgnRegexp} from "/@lib@/wsp/search.js";
import {ItemType} from "/@lib@/wsp/wspMetaUi.js";
import {JRemoteResDescFromClient} from "/@lib@/core/remote.js";
import {TxtMetaRemote} from "/@back@/wsp/widgets/wed/txtMetaRemote.js";
import {DomToDomUpdater} from "/@lib@/commons/xml/domToDomUpdater.js";
import IS_element = DOM.IS_element;


/** [table] gestionnaire des rowSpan / colSpan des cellules. */
export const spanMgr: ITxtCellSpanMgr = {

	refreshSpan(this: void, cell: TxtCell): void {
		DOM.setAttr(cell, 'rowspan', cell.wedAttributes ? cell.wedAttributes.rowSpan : null);
		DOM.setAttr(cell, 'colspan', cell.wedAttributes ? cell.wedAttributes.colSpan : null);
	},

	isRowSpanAvailable(cell: TxtCell): boolean {return true},

	isColSpanAvailable(cell: TxtCell): boolean {return true},

	updateRowSpan(cell: TxtCell, span: number, batch: IDocBatch): void {
		batch.add(new XmlStrMsg().init(XA.append(cell.wedAnchor, 'rowSpan'), span > 1 ? span.toString(10) : null));
	},

	updateColSpan(cell: TxtCell, span: number, batch: IDocBatch): void {
		batch.add(new XmlStrMsg().init(XA.append(cell.wedAnchor, 'colSpan'), span > 1 ? span.toString(10) : null));
	},

	splitCell(cell: TxtCell, rowSpan: number, colSpan: number): IJmlSet {
		const c = cell.exportWrap(null);
		if (rowSpan > 1) (c[0] as IJmlObj)['rowSpan'] = rowSpan.toString();
		if (colSpan > 1) (c[0] as IJmlObj)['colSpan'] = colSpan.toString();
		return c;
	}

};

/** [table] gestionnaire de la largeur des colonnes. */
export const widthColMgr: ITxtColWidthMgr = {

	refreshW(this: void, col: TxtCol) {
		const w = col.wedAttributes && col.wedAttributes.width ? col.wedAttributes.width + 'ch' : '2ch';
		DOM.setStyle(col, 'width', w);
	},

	getLogicW(this: void, col: TxtCol): number {
		return parseInt(col.wedAttributes.width, 10) || 2;
	},

	getLogicBounds(this: void, col: TxtCol): { min?: number, max?: number, step?: number } {
		return COL_BOUNDS;
	},

	updateLogicW(col: TxtCol, width: number, batch: IDocBatch): void {
		batch.add(new XmlStrMsg().init(XA.append(col.wedAnchor, 'width'), width.toString(10)));
	},

	fillDefault(colMode: TxtColModel, jml: IJmlObj): void {
		jml.width = '10';
	}
};

const COL_BOUNDS = {min: 2};

/** Ouverture du panel de l'élément à sa création pour renseigner les metadonnées. */
export const openPanel: ITxtOnCreateHook = async function (this: TxtModel, parent: ITxtElement, newJml?: IJmlSet, postInject?: (docHolder: DocHolder) => void): Promise<IJmlSet> {
	const wedlet = parent instanceof TxtElement ? parent : parent.wedlet;
	const wedMgr = wedlet.wedMgr;
	const skParentRule = wedMgr.docHolder.getStruct(parent.wedAnchor, WEDLET.buildVirtualPath(wedlet)) as SkRuleNode;
	let skRule: SkRuleNode;
	if (!newJml) {
		skRule = skParentRule.contentRule.findRuleNodeFor(this.newJml());
		if (!skRule) return;
		newJml = [];
		skRule.createContent(newJml); //skRule.createContent() ajoute les subStructs required contrairement à this.newJml().
	} else if (!skRule) {
		skRule = skParentRule.contentRule.findRuleNodeFor(newJml[0] as IJmlObj);
		if (!skRule) return;
	}
	if ((wedMgr.wedEditor as IWedEditorMetaBar).metaBarMode === "shown") return newJml; //édition directe des metas dans l'écran principal.
	const wsp = (wedMgr.reg.env as IWspEnv).wsp;
	const house = await wsp.wspServer.wspsLive.newLocalHouse({
		initialJml: newJml,
		schema: wedMgr.docHolder.house.schemaDom.schema,
		schemaDomConfig: {rootRule: new SkRuleDoc().init(skRule)},
		buildOptions: {autoComplete: true}
	}, wsp);
	try {
		const docHolder = new DocHolder(house);
		await METAS.openMetaNew(wedMgr, docHolder, this.panelModel, `Insertion de : ${this.nodeLabel}`, parent.wedlet);
		if (postInject) {postInject(docHolder)}
		return docHolder.getContent();
	} finally {
		wsp.wspServer.wspsLive.removeLocalHouse(house);
	}

};

/**
 * Ouverture du panel de l'élément de type lien externe à sa création pour renseigner les metadonnées.
 * - Si du texte est sélectionné qui ressemble à une url recopie ce texte dans le 1er champs des métas typé "property/url".
 * - Si aucun texte n'est sélectionné, en sortie d'édition recopie le champ url dans le contenu de l'élément créé.
 */
export function onCreateUrlLink(this: TxtModel, parent: ITxtElement) {
	const newJml = [this.newJml()] as IJmlSet;
	const selMgr = parent.txtRoot.selMgr;
	if (selMgr.type === "Range" && selMgr.anchorNode instanceof Text && selMgr.anchorNode === selMgr.focusNode) {
		//On a un texte sélectionné : si ca ressemble à une url, on l'injecte.
		const txt = selMgr.anchorNode.nodeValue.substring(selMgr.range.startOffset, selMgr.range.endOffset);
		if (!/\s/.test(txt) && /\./.test(txt)) {
			//ne contient pas d'espace et au moins un '.', pourrait être une url... XXX règle plus stricte ?
			const parentRule = parent.wedlet.wedMgr.docHolder.getStruct(parent.wedAnchor) as SkRuleElt;
			if (parentRule) {
				const tagRule = parentRule.contentRule.findRuleNodeFor(newJml[0] as IJmlNode) as SkRuleElt;
				if (tagRule) {
					const metaRule = tagRule.contentRule.findRuleNodeFor({'': this.metasName}) as SkRuleElt;
					if (metaRule) {
						const urlProp = metaRule.contentRule.findRule((r: SkRule) => r.skFamily === "property/url") as SkRuleElt;
						if (urlProp) {
							const propElt = urlProp.createNode(DOM.sharedXmlDoc());
							propElt.textContent = txt;
							const metaElt = metaRule.createNode(propElt);
							metaElt.appendChild(propElt);
							newJml.push(JML.dom2jml(metaElt));
						}
					}
				}
			}
		}
	}
	return openPanel.call(this, parent, newJml, (docHolder: DocHolder) => {
		selMgr.restoreSel();
		if (selMgr.type === "Caret") {
			//Insertion sur sel vide, on recopie l'URL dans le contenu inline.
			//On recherche l'URL
			let url: string;
			const schemaDom = docHolder.house.schemaDom;
			const doc = docHolder.getDocument();
			let elt = doc.documentElement.firstElementChild as Element;
			while (elt) {
				if (schemaDom.getSkNode(elt).rule.skFamily === "property/url") {
					url = elt.textContent;
					break;
				}
				elt = DOM.findNext(elt, null, IS_element);
			}
			if (url) doc.documentElement.appendChild(doc.createTextNode(url));
		}
	});
}


/**
 * Service de fourniture d'un titre de {@see OTxtMetaRemoteInit.buildTitle}, en fonction d'un xpath exécuté sur le resMeta passé en paramètre
 * @param fieldParaXpath
 * @param target
 * @param txtElt
 * @param resMetas
 */
export function remoteResDataFormFieldToHtmlTitle(this: TxtMetaRemote, fieldParaXpath: string, target: HTMLSpanElement, txtElt: TxtElement, resMetas: JRemoteResDescFromClient | null): 'done' | void {
	const xpath = fieldParaXpath ? new XPathEvaluator().createExpression(fieldParaXpath, (prefix) => DOM.lookupNamespaceURI(txtElt.metas, prefix)) : null;
	const para = txtElt.metas ? xpath?.evaluate(txtElt.metas, XPathResult.FIRST_ORDERED_NODE_TYPE) : null;
	if (!para?.singleNodeValue?.firstChild) return;
	new DomToDomUpdater(target, function (xml) {
		for (let ch = xml.firstChild; ch; ch = ch.nextSibling) {
			if (ch instanceof Text)
				this.addText(ch.nodeValue);
			else if (ch instanceof Element && ch.namespaceURI === DOM.SCCORE_NS)
				this.addElt("span", {"class": ch.getAttribute("role")}, ch, this.buildRoot);
			else if (ch instanceof Element) {
				this.buildRoot.call(this, ch)
			}
		}
	}).refresh(para.singleNodeValue);
	return target.hasChildNodes() ? 'done' : null;
}


/** Ouverture d'un écran de sélection/création d'un item pour renseigner le lien du tag. */
export const selectItem: ITxtOnCreateHook = async function (this: TxtModel, parent: ITxtElement): Promise<IJmlSet> {
	const wedlet = parent instanceof TxtElement ? parent : parent.wedlet;
	const wedMgr = wedlet.wedMgr;
	const skParentRule = wedMgr.docHolder.getStruct(parent.wedAnchor, WEDLET.buildVirtualPath(wedlet)) as SkRuleNode;
	const newObj = this.newJml() as any;
	const skTagRule = skParentRule.contentRule.findRuleNodeFor(newObj);
	const skLinkRule = skTagRule.contentRule.findRuleAttrFor("sc:refUri") as IPtrItemRule;
	const willAddTitle = this instanceof InlLinkModel && parent.txtRoot.selMgr.type === "Caret"; //avant affichage fenetre sinon parent.txtRoot.selMgr pas encore restaure
	const {FastFindItem} = await import("/@back@/wsp/dialogs/fastFindItem.js");
	const sd = await FastFindItem.openFastFind({
		reg: wedMgr.reg,
		restrictions: skLinkRule.itSgnPattern ? new SearchItemSgnRegexp(skLinkRule.itSgnPattern.source) : null,
		itemCreator: wedMgr.reg.getPref("wsp.createItem", true) ? ItemCreator.setDefaultConfigFromReg(wedMgr.reg, {
			spaceUri: ITEM.extractSpaceUri((wedMgr.reg.env as IItemUiEnv).longDesc.srcUri),
			itemTypesTree: {
				itemTypeReducer: skLinkRule.itSgnPattern ? (acc: ItemType[], cur: ItemType) => {
					if (skLinkRule.itSgnPattern.test(cur.getSgn())) acc.push(cur);
					return acc;
				} : null,
			}
		}) : undefined,
		subItemsFind: skLinkRule.subItSgnPattern ? {subItemSgnPattern: skLinkRule.subItSgnPattern} : undefined
	}, wedMgr.wedEditor)?.onNextClose();
	if (!sd) return null;
	newObj['sc:refUri'] = ITEM.srcRefSub(sd);
	if (willAddTitle) {
		//Si ULink sans selection à wrapper : on injecte le titre ou le code de l'item
		return [newObj, [sd.itSubItem?.ti || sd.itTi || SRC.extractLeafFromUri(sd.srcUri)]];
	}
	return [newObj];
};

/** Ouverture d'un écran de sélection/création d'un item pour renseigner le lien du tag PUIS ouverture du panel des metas. */
export const selectItemAndOpenPanel: ITxtOnCreateHook = async function (this: TxtModel, parent: ITxtElement): Promise<IJmlSet> {
	const jmlSet = await selectItem.call(this, parent);
	if (!jmlSet) return null;
	return openPanel.call(this, parent, jmlSet);
};


